import React, { useEffect, useState, useCallback } from 'react';
import { Row, Col, Button, Input, Label, Card, CardHeader, CardBody, Badge, Spinner, Alert } from 'reactstrap';
import Select from 'react-select';
import { Service } from './Service';

const Recipies = () => {
    const service = React.useMemo(() => new Service(), []);
    const [recipies, setRecipies] = useState([]);
    const [search, setSearch] = useState('');
    const [loading, setLoading] = useState(false);
    const [ingredients, setIngredients] = useState([]);
    const [items, setItems] = useState([]);
    const [alertInfo, setAlertInfo] = useState(null);

    const [recipy, setRecipy] = useState({
        id: '',
        title: '',
        itemCode: '',
        description: '',
        qty: 0,
        ingredients: []
    });

    const loadAllData = useCallback(async (selectId = null) => {
        try {
            setLoading(true);
            const [recs, ings, prds] = await Promise.all([
                service.getRecipies(),
                service.getIngredients(),
                service.getProducts()
            ]);
            setRecipies(recs || []);
            setIngredients(ings || []);
            setItems(prds || []);
            
            if (recs && recs.length > 0) {
                if (selectId) {
                    const found = recs.find(r => r.id === selectId);
                    setRecipy(found || recs[0]);
                } else {
                    setRecipy(prev => {
                        if (prev && prev.id) {
                            const stillExists = recs.find(r => r.id === prev.id);
                            return stillExists || recs[0];
                        }
                        return recs[0];
                    });
                }
            } else {
                setRecipy({
                    id: '',
                    title: '',
                    itemCode: '',
                    description: '',
                    qty: 0,
                    ingredients: []
                });
            }
        } catch (error) {
            console.error('Error loading recipes data:', error);
            setAlertInfo({ type: 'danger', message: 'Σφάλμα κατά τη φόρτωση των δεδομένων συνταγών.' });
        } finally {
            setLoading(false);
        }
    }, [service]);

    useEffect(() => {
        loadAllData();
    }, [loadAllData]);

    const addRecipy = async () => {
        try {
            setLoading(true);
            const newRecipy = {
                title: 'Νέα Συνταγή',
                itemCode: '',
                description: '',
                qty: 1000,
                ingredients: []
            };

            const createdRecipy = await service.addRecipy(newRecipy);
            setAlertInfo({ type: 'success', message: 'Η νέα συνταγή δημιουργήθηκε επιτυχώς!' });
            await loadAllData(createdRecipy?.id);
        } catch (error) {
            console.error('Error adding recipe:', error);
            setAlertInfo({ type: 'danger', message: 'Σφάλμα κατά τη δημιουργία της συνταγής.' });
        } finally {
            setLoading(false);
        }
    };

    const saveRecipy = async () => {
        if (!recipy) return;
        if (!recipy.title || !recipy.title.trim()) {
            setAlertInfo({ type: 'warning', message: 'Παρακαλούμε εισάγετε όνομα για τη συνταγή.' });
            return;
        }

        try {
            setLoading(true);
            const saved = recipy.id ? await service.updateRecipy(recipy) : await service.addRecipy(recipy);
            setAlertInfo({ type: 'success', message: 'Η συνταγή αποθηκεύτηκε επιτυχώς!' });
            await loadAllData(saved?.id || recipy.id);
        } catch (error) {
            console.error('Error saving recipe:', error);
            setAlertInfo({ type: 'danger', message: 'Σφάλμα κατά την αποθήκευση της συνταγής.' });
        } finally {
            setLoading(false);
        }
    };

    const deleteRecipy = async (id) => {
        if (!id) return;
        if (!window.confirm('Είστε σίγουροι ότι θέλετε να διαγράψετε αυτή τη συνταγή;')) return;
        try {
            setLoading(true);
            const success = await service.deleteRecipy(id);
            if (success) {
                setAlertInfo({ type: 'success', message: 'Η συνταγή διαγράφηκε επιτυχώς!' });
                const remaining = recipies.filter(r => r.id !== id);
                setRecipies(remaining);
                setRecipy(remaining.length > 0 ? remaining[0] : {
                    id: '',
                    title: '',
                    itemCode: '',
                    description: '',
                    qty: 0,
                    ingredients: []
                });
            } else {
                setAlertInfo({ type: 'danger', message: 'Η διαγραφή της συνταγής απέτυχε.' });
            }
        } catch (error) {
            console.error('Error deleting recipe:', error);
            setAlertInfo({ type: 'danger', message: 'Σφάλμα κατά τη διαγραφή της συνταγής.' });
        } finally {
            setLoading(false);
        }
    };

    const removeIngredient = (index) => {
        if (!recipy.ingredients) return;
        const newIngs = recipy.ingredients.filter((_, idx) => idx !== index);
        setRecipy({ ...recipy, ingredients: newIngs });
    };

    const addIngredientRow = () => {
        const currentIngs = recipy.ingredients || [];
        setRecipy({
            ...recipy,
            ingredients: [
                ...currentIngs,
                { ingredientId: '', qty: 0 }
            ]
        });
    };

    const totalIngredientsWeight = (recipy.ingredients || [])
        .filter(Boolean)
        .reduce((sum, item) => sum + (Number(item.qty) || 0), 0);

    const filteredRecipies = recipies.filter(r => 
        !search || 
        ((r.title && r.title.toLowerCase().includes(search.toLowerCase())) || 
         (r.description && r.description.toLowerCase().includes(search.toLowerCase())) || 
         (r.itemCode && r.itemCode.toLowerCase().includes(search.toLowerCase())))
    );

    return (
        <div>
            {alertInfo && (
                <Alert 
                    color={alertInfo.type} 
                    className="d-flex align-items-center justify-content-between mb-4 shadow-sm"
                    toggle={() => setAlertInfo(null)}
                >
                    <div className="d-flex align-items-center">
                        <i className={`bi ${alertInfo.type === 'success' ? 'bi-check-circle-fill text-success' : alertInfo.type === 'warning' ? 'bi-exclamation-triangle-fill text-warning' : 'bi-x-circle-fill text-danger'} me-2 fs-5`}></i>
                        <span>{alertInfo.message}</span>
                    </div>
                </Alert>
            )}

            <Row className="g-4">
                <Col lg="4" md="5">
                    <div className="ek-card mb-0">
                        <div className="ek-card-header">
                            <h5>
                                <i className="bi bi-journal-bookmark-fill text-primary"></i> Συνταγές
                            </h5>
                            <Badge color="info" pill>{recipies.length}</Badge>
                        </div>
                        <div className="ek-card-body">
                            <Button onClick={addRecipy} block color="primary" className="mb-3" disabled={loading}>
                                <i className="bi bi-plus-lg"></i> Προσθήκη Συνταγής
                            </Button>

                            <div className="ek-search-box">
                                <i className="bi bi-search"></i>
                                <Input
                                    type="search"
                                    className="ek-search-input"
                                    placeholder="Αναζήτηση συνταγής..."
                                    value={search}
                                    onChange={(e) => setSearch(e.target.value)}
                                />
                            </div>

                            <div className="scrollable-list">
                                {loading && recipies.length === 0 ? (
                                    <div className="text-center py-4">
                                        <Spinner size="sm" color="primary" />
                                        <span className="ms-2 text-muted">Φόρτωση...</span>
                                    </div>
                                ) : filteredRecipies.length === 0 ? (
                                    <div className="ek-empty-state py-4">
                                        <i className="bi bi-inbox fs-2"></i>
                                        <p className="small text-muted mb-0">Δεν βρέθηκαν συνταγές</p>
                                    </div>
                                ) : (
                                    <div className="ek-list-group">
                                        {filteredRecipies.map(r => (
                                            <div
                                                key={r.id || r.title}
                                                className={`ek-list-item ${recipy && recipy.id === r.id ? 'active' : ''}`}
                                                onClick={() => setRecipy(r)}
                                            >
                                                <div>
                                                    <div className="ek-list-item-title">{r.title || 'Χωρίς Τίτλο'}</div>
                                                    <div className="ek-list-item-sub">
                                                        <i className="bi bi-speedometer2"></i>
                                                        <span>{r.qty || 0} gr</span>
                                                        {r.ingredients && (
                                                            <span className="badge bg-light text-dark ms-1">
                                                                {r.ingredients.length} συστ.
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>
                                                <i className="bi bi-chevron-right text-muted" style={{ fontSize: '0.8rem' }}></i>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </Col>

                <Col lg="8" md="7">
                    {recipy && recipy.id ? (
                        <Card className="ek-card">
                            <CardHeader className="ek-card-header">
                                <h5>
                                    <i className="bi bi-pencil-square text-primary"></i> 
                                    {recipy.title || 'Επεξεργασία Συνταγής'}
                                </h5>
                                <div className="d-flex align-items-center gap-2">
                                    <Badge className="badge-brand">{recipy.qty || 0} gr</Badge>
                                </div>
                            </CardHeader>
                            <CardBody className="ek-card-body">
                                <Row>
                                    <Col md="6">
                                        <div className="ek-form-group mb-3">
                                            <Label className="ek-label">
                                                <i className="bi bi-journal-text"></i> Όνομα Συνταγής
                                            </Label>
                                            <Input
                                                value={recipy.title || ''}
                                                placeholder="π.χ. Συνταγή Ψωμιού"
                                                onChange={(e) => setRecipy({ ...recipy, title: e.target.value })}
                                            />
                                        </div>
                                    </Col>
                                    <Col md="6">
                                        <div className="ek-form-group mb-3">
                                            <Label className="ek-label">
                                                <i className="bi bi-box-seam"></i> Αντίστοιχο Προϊόν
                                            </Label>
                                            <Select
                                                className="react-select-container"
                                                classNamePrefix="react-select"
                                                value={items.find(i => i.code === recipy.itemCode) ? { 
                                                    value: recipy.itemCode, 
                                                    label: `${items.find(i => i.code === recipy.itemCode).code} - ${items.find(i => i.code === recipy.itemCode).title}` 
                                                } : null}
                                                onChange={(selectedOption) => setRecipy({ ...recipy, itemCode: selectedOption ? selectedOption.value : '' })}
                                                options={items.map(item => ({ value: item.code, label: `${item.code} - ${item.title}` }))}
                                                isClearable
                                                placeholder="Επιλέξτε Προϊόν..."
                                            />
                                        </div>
                                    </Col>
                                </Row>

                                <Row>
                                    <Col md="6">
                                        <div className="ek-form-group mb-3">
                                            <Label className="ek-label">
                                                <i className="bi bi-speedometer2"></i> Ποσότητα Βάσης Συνταγής (gr)
                                            </Label>
                                            <Input
                                                type="number"
                                                step="any"
                                                value={recipy.qty || ''}
                                                placeholder="π.χ. 1000"
                                                onChange={(e) => setRecipy({ ...recipy, qty: e.target.valueAsNumber || 0 })}
                                            />
                                        </div>
                                    </Col>
                                    <Col md="6">
                                        <div className="ek-form-group mb-3">
                                            <Label className="ek-label">
                                                <i className="bi bi-card-text"></i> Περιγραφή / Σημειώσεις
                                            </Label>
                                            <Input
                                                value={recipy.description || ''}
                                                placeholder="Προαιρετικές οδηγίες συνταγής..."
                                                onChange={(e) => setRecipy({ ...recipy, description: e.target.value })}
                                            />
                                        </div>
                                    </Col>
                                </Row>

                                <div className="my-4 pt-3 border-top">
                                    <div className="d-flex align-items-center justify-content-between mb-3">
                                        <div>
                                            <h5 className="mb-0 text-dark fw-bold">
                                                <i className="bi bi-pie-chart-fill text-primary me-2"></i> Συστατικά Συνταγής
                                            </h5>
                                            <small className="text-muted">
                                                Συνολικό βάρος συστατικών: <strong>{totalIngredientsWeight} gr</strong>
                                            </small>
                                        </div>
                                        <Button color="primary" size="sm" onClick={addIngredientRow}>
                                            <i className="bi bi-plus-lg"></i> Νέο Συστατικό
                                        </Button>
                                    </div>

                                    {(!recipy.ingredients || recipy.ingredients.length === 0) ? (
                                        <div className="ek-empty-state py-4 border rounded-3 bg-light">
                                            <i className="bi bi-basket3 text-muted fs-3"></i>
                                            <p className="small text-muted mb-2">Δεν έχουν προστεθεί συστατικά στη συνταγή</p>
                                            <Button color="outline-primary" size="sm" onClick={addIngredientRow}>
                                                <i className="bi bi-plus-lg"></i> Προσθήκη Πρώτου Συστατικού
                                            </Button>
                                        </div>
                                    ) : (
                                        <div className="d-flex flex-column gap-2 mb-3">
                                            {recipy.ingredients.filter(Boolean).map((ingredient, index) => {
                                                const matchedIng = ingredients.find(i => i.id === ingredient.ingredientId);
                                                return (
                                                    <div key={index} className="p-3 bg-light rounded-3 border d-flex align-items-center gap-3">
                                                        <div className="badge bg-secondary rounded-circle p-2" style={{ width: 28, height: 28, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                            {index + 1}
                                                        </div>
                                                        <div className="flex-grow-1">
                                                            <Select
                                                                className="react-select-container"
                                                                classNamePrefix="react-select"
                                                                value={matchedIng ? { value: matchedIng.id, label: matchedIng.title } : null}
                                                                onChange={(selectedOption) => {
                                                                    const id = selectedOption ? selectedOption.value : '';
                                                                    const updated = recipy.ingredients.map((item, idx) => 
                                                                        idx === index ? { ...item, ingredientId: id } : item
                                                                    );
                                                                    setRecipy({ ...recipy, ingredients: updated });
                                                                }}
                                                                options={ingredients.map(ing => ({ value: ing.id, label: ing.title }))}
                                                                isClearable
                                                                placeholder="Επιλέξτε Συστατικό..."
                                                            />
                                                        </div>
                                                        <div style={{ width: '160px' }}>
                                                            <div className="input-group mb-0">
                                                                <Input
                                                                    type="number"
                                                                    step="any"
                                                                    placeholder="Ποσότητα"
                                                                    value={ingredient.qty || ''}
                                                                    onChange={(e) => {
                                                                        const qty = e.target.valueAsNumber || 0;
                                                                        const updated = recipy.ingredients.map((item, idx) => 
                                                                            idx === index ? { ...item, qty } : item
                                                                        );
                                                                        setRecipy({ ...recipy, ingredients: updated });
                                                                    }}
                                                                />
                                                                <span className="input-group-text">gr</span>
                                                            </div>
                                                        </div>
                                                        <Button
                                                            color="outline-danger"
                                                            size="sm"
                                                            onClick={() => removeIngredient(index)}
                                                            title="Διαγραφή συστατικού"
                                                        >
                                                            <i className="bi bi-trash3"></i>
                                                        </Button>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>

                                <div className="d-flex gap-2 pt-3 border-top">
                                    <Button onClick={saveRecipy} color="primary" disabled={loading}>
                                        <i className="bi bi-check2-circle"></i> Αποθήκευση Συνταγής
                                    </Button>
                                    <Button onClick={() => deleteRecipy(recipy.id)} color="danger" disabled={loading}>
                                        <i className="bi bi-trash3"></i> Διαγραφή
                                    </Button>
                                </div>
                            </CardBody>
                        </Card>
                    ) : (
                        <div className="ek-card">
                            <div className="ek-empty-state">
                                <i className="bi bi-journal-bookmark"></i>
                                <h4>Επιλέξτε μία συνταγή</h4>
                                <p>Επιλέξτε μία συνταγή από την αριστερή λίστα για επεξεργασία ή πατήστε "Προσθήκη Συνταγής" για νέα.</p>
                            </div>
                        </div>
                    )}
                </Col>
            </Row>
        </div>
    );
};

export { Recipies };
export default Recipies;
